import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import {
  API_CCM_PATIENTS_MANUAL_INTERACTIVE_TIME,
  API_PATIENTS_MANUAL_INTERACTIVE_TIME,
} from 'services/dataService/resources';
import { showResult } from 'infrastructure/hooks/utils';
import { DateFormatter } from 'infrastructure/functions';

import { getRpmInteractionTimeKey } from './get-rpm-interactive-time';
import { getCcmInteractionTimeKey } from './get-ccm-interactive-time';

import type { Res } from './get-rpm-interactive-time';
import type { UseMutateFunction } from '@tanstack/react-query';

export interface DataProps {
  patientGuid: string;
  isRpm: boolean;
  timeInMinutes: number;
  comment: string;
}

interface Response {
  guid: string;
  patientGuid: string;
  timeInMinutes: number;
  comment: string;
  createdAt: string;
}

export type TSetManualInteractionTime = UseMutateFunction<
  Response | undefined,
  unknown,
  DataProps,
  unknown
>;

interface Props {
  onSuccess: () => void;
}

export const useSetManualInteractionTime = ({ onSuccess }: Props) => {
  const today = new Date();
  const formattedDate = DateFormatter({ date: today });
  const queryClient = useQueryClient();

  const { mutate, ...other } = useMutation({
    mutationFn: async (postData: DataProps) => {
      const url = postData.isRpm
        ? API_PATIENTS_MANUAL_INTERACTIVE_TIME(postData.patientGuid)
        : API_CCM_PATIENTS_MANUAL_INTERACTIVE_TIME(postData.patientGuid);

      const { error, data } = await dataService.createOne<Response>(url, {
        timeInMinutes: postData.timeInMinutes,
        comment: postData.comment,
      });

      if (error) {
        showResult(error);
        throw new Error(error);
      }

      return data;
    },
    onSuccess(res, vars) {
      onSuccess();
      showResult();

      const rpmTimeKey = getRpmInteractionTimeKey(
        vars.patientGuid,
        formattedDate,
      );
      const ccmTimeKey = getCcmInteractionTimeKey(
        vars.patientGuid,
        formattedDate,
      );
      const queryKey = vars.isRpm ? rpmTimeKey : ccmTimeKey;
      queryClient.setQueryData<Res>(queryKey, (old) => {
        if (!old || !res) return old;

        old.clinicTime += res.timeInMinutes * 60;
        old.totalTime += res.timeInMinutes * 60;

        return old;
      });
    },
  });

  return { setManualInteractionTime: mutate, ...other };
};
